import React from "react";
import {
  Box,
  VStack,
  HStack,
  Input,
  Textarea,
  Select,
  FormControl,
  FormLabel,
  Checkbox,
  Stack,
  Heading,
  Switch,
  Button,
  Flex,
  Text,
  RadioGroup,
  Radio,
} from "@chakra-ui/react";
import { FiBook } from "react-icons/fi";
import { DarwinAgent, DarwinTool } from "build-ts/darwin_agent";
import { DarwinAgentType } from "build-ts/common";

interface EvaLeftPanelProps {
  agent: DarwinAgent;
  availableTools: DarwinTool[];
  onConfigChange: (field: string, value: any) => void;
  onToolsChange: (selectedToolsArray: DarwinTool[]) => void;
  isEditing?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  onSave: () => void;
  isCreateMode?: boolean;
  onViewDocs?: () => void;
}

const EvaLeftPanel: React.FC<EvaLeftPanelProps> = ({
  agent,
  availableTools,
  onConfigChange,
  onToolsChange,
  isEditing = false,
  onEdit,
  onCancel,
  onSave,
  isCreateMode = false,
  onViewDocs,
}) => {

  return (
    <Box className="eva-left-panel eva-small-scrollbar">
      <VStack spacing={6} align="stretch">
        {/* Basic Agent Info */}
        <Box className="eva-section-card">
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="xs" color="gray.700" mb={0}>
              Agent Config
            </Heading>
            {isCreateMode ? (
                <HStack spacing={2}>
                  <Button size="sm" variant="outline" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={onSave}
                  >
                    Create Agent
                  </Button>
                </HStack>
              ) : !isEditing ? (
                <Button size="sm" colorScheme="blue" onClick={onEdit}>
                  Edit Agent
                </Button>
              ) : (
                <HStack spacing={2}>
                  <Button size="sm" variant="outline" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={onSave}
                  >
                    Save
                  </Button>
                </HStack>
              )}
          </Flex>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Agent Name</FormLabel>
              <Input
                value={agent.header?.name || ""}
                onChange={(e) => onConfigChange("header.name", e.target.value)}
                placeholder="Enter agent name"
                isDisabled={!isEditing && !isCreateMode}
                color={!isEditing && !isCreateMode ? "gray.500" : "black"}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={agent.header?.description || ""}
                onChange={(e) => onConfigChange("header.description", e.target.value)}
                placeholder="Describe the agent's purpose and notes"
                rows={5}
                isDisabled={!isEditing && !isCreateMode}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Agent Type</FormLabel>
              {isCreateMode ? (
                <Select
                  value={agent.header?.type || ""}
                  onChange={(e) => onConfigChange("header.type", e.target.value)}
                  placeholder="Select agent type"
                >
                  <>
                    {Object.values(DarwinAgentType)
                      .filter(
                        value => typeof value === "string" && value !== "UNRECOGNIZED" && value !== "UNKNOWN_AGENT_TYPE"
                      )
                      .map((agentType) => (
                        <option key={agentType} value={agentType}>
                          {agentType}
                        </option>
                      ))}
                    </>
                </Select>
              ) : (
                <Input
                  value={agent.header?.type || ""}
                  readOnly
                  bg="gray.50"
                  color="gray.600"
                />
              )}
            </FormControl>

            <FormControl>
              <FormLabel>
                Is Agent Active
                <Switch
                  isChecked={agent.header?.is_active || false}
                  onChange={(e) => onConfigChange("header.is_active", e.target.checked)}
                  colorScheme="blue"
                  margin={3}
                  isDisabled={!isEditing && !isCreateMode}
                />
              </FormLabel>
            </FormControl>
          </VStack>

          {/* Tools Selection */}
          <Heading size="xs" color="gray.700" mb={4} mt={4}>
            Available Tools
          </Heading>

          {/* Group tools by is_response_tool */}
          {(() => {
            const regularTools = availableTools.filter(tool => !tool.header?.is_response_tool);
            const responseTools = availableTools.filter(tool => tool.header?.is_response_tool);

            return (
              <VStack align="stretch">
                {/* Regular Tools (Checkboxes) */}
                {regularTools.length > 0 && (
                  <Box>
                    <Stack spacing={2}>
                      {regularTools.map((tool) => {
                        const toolId = tool.header?.id;
                        const toolName = tool.header?.name;
                        const isChecked = agent.tools?.some(selectedTool => {
                          const selectedToolId = selectedTool.header?.id;
                          return selectedToolId === toolId;
                        }) || false;

                        return (
                          <Checkbox
                            key={toolId}
                            id={toolId}
                            value={toolId}
                            isChecked={isChecked}
                            colorScheme="blue"
                            size="md"
                            isDisabled={!isEditing && !isCreateMode}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              const currentTools = (agent.tools || []) as DarwinTool[];
                              if (isChecked) {
                                // keep array type consistent by mapping to the same type as availableTools
                                const next = [...currentTools, tool] as DarwinTool[];
                                onToolsChange(next as any);
                              } else {
                                const filtered = currentTools.filter(currentTool => {
                                  const currentToolId = currentTool.header?.id;
                                  return currentToolId !== toolId;
                                });
                                onToolsChange(filtered as any);
                              }
                            }}
                          >
                            {toolName}
                          </Checkbox>
                        );
                      })}
                    </Stack>
                  </Box>
                )}

                {/* Response Tools (Radio Buttons) */}
                {responseTools.length > 0 && (
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color="gray.700"
                      mb={2}
                      textAlign="left"
                    >
                      Response Tools
                    </Text>
                    <RadioGroup
                      value={(() => {
                        const selectedResponseTool = agent.tools?.find(selectedTool => {
                          return responseTools.some(responseTool =>
                            responseTool.header?.id === selectedTool.header?.id
                          );
                        });
                        return selectedResponseTool?.header?.id || "";
                      })()}
                      onChange={(value) => {
                        const currentTools = (agent.tools || []) as DarwinTool[];
                        // Remove any existing response tools
                        const filteredTools = currentTools.filter(currentTool => {
                          return !responseTools.some(responseTool =>
                            responseTool.header?.id === currentTool.header?.id
                          );
                        });

                        if (value) {
                          // Select new response tool
                          const selectedTool = responseTools.find(tool => tool.header?.id === value);
                          if (selectedTool) {
                            const next = [...filteredTools, selectedTool] as DarwinTool[];
                            onToolsChange(next as any);
                          }
                        } else {
                          onToolsChange(filteredTools as any);
                        }
                      }}
                      isDisabled={!isEditing && !isCreateMode}
                    >
                      <Stack spacing={2} justifySelf="flex-start">
                        {responseTools.map((tool) => {
                          const toolId = tool.header?.id;
                          const toolName = tool.header?.name;
                          const isSelected = agent.tools?.some(selectedTool => {
                            return selectedTool.header?.id === toolId;
                          }) || false;

                          return (
                            <Box
                              key={toolId}
                              onClick={(e) => {
                                // Allow deselection by clicking on already selected radio button
                                if (isSelected && (isEditing || isCreateMode)) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const currentTools = (agent.tools || []) as DarwinTool[];
                                  const filteredTools = currentTools.filter(currentTool => {
                                    return currentTool.header?.id !== toolId;
                                  });
                                  onToolsChange(filteredTools as any);
                                }
                              }}
                              cursor={isSelected && (isEditing || isCreateMode) ? "pointer" : "default"}
                            >
                              <Radio
                                value={toolId}
                                colorScheme="blue"
                                size="md"
                              >
                                {toolName}
                              </Radio>
                            </Box>
                          );
                        })}
                      </Stack>
                    </RadioGroup>
                  </Box>
                )}
              </VStack>
            );
          })()}
        </Box>

        {/* Documentation Section */}
        <Box className="eva-section-card">
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="xs" color="gray.700">
              Documentation
            </Heading>
          </Flex>
          <VStack spacing={3} align="stretch">
            <Text fontSize="sm" color="gray.600" mb={2}>
              Access system prompt variable documentation.
            </Text>
            <Button
              leftIcon={<FiBook />}
              size="sm"
              variant="outline"
              onClick={onViewDocs}
              colorScheme="blue"
              borderColor="blue.300"
              _hover={{ borderColor: "blue.400", bg: "blue.50" }}
            >
              View Variable Docs
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default EvaLeftPanel;
